import { useCallback } from 'react';
import type { HTMLAttributes, JSX, Key, SyntheticEvent } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type {
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';

import { useIdOptionsResolver } from './hooks/useIdOptionsResolver.hook';
import type { IdOption } from './types';

interface Props<TId extends Key, TOption extends IdOption<TId>> {
  readonly label: string;
  readonly loading?: boolean;
  readonly onChange: (newValue: readonly TId[]) => void;
  readonly options: readonly TOption[];
  readonly renderOption?: (
    props: HTMLAttributes<HTMLLIElement> & Readonly<{ key: Key }>,
    option: TOption,
    state: AutocompleteRenderOptionState,
  ) => JSX.Element;
  readonly value: readonly TId[];
}

export const IdMultiAutocomplete = <
  TId extends Key,
  TOption extends IdOption<TId>,
>({
  label,
  loading = false,
  onChange,
  options,
  renderOption,
  value,
}: Readonly<Props<TId, TOption>>) => {
  const { getOptionDisabled, getOptionLabel, ids, optionById } =
    useIdOptionsResolver<TId, TOption>(options);

  const handleChange = useCallback(
    (_event: Readonly<SyntheticEvent>, newValue: readonly TId[]) => {
      onChange(newValue);
    },
    [onChange],
  );

  const renderInput = useCallback(
    (params: Readonly<AutocompleteRenderInputParams>) => (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <TextField {...params} label={label} />
    ),
    [label],
  );

  const resolvedRenderOption = useCallback(
    (
      itemProps: HTMLAttributes<HTMLLIElement> & Readonly<{ key: Key }>,
      id: TId,
      state: AutocompleteRenderOptionState,
    ) => {
      const option = optionById.get(id);

      if (!option) {
        return null;
      }

      if (renderOption) {
        return renderOption(itemProps, option, state);
      }

      const { key, ...rest } = itemProps;

      return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <li key={key} {...rest}>
          {option.label}
        </li>
      );
    },
    [optionById, renderOption],
  );

  return (
    <Autocomplete<TId, true>
      disableCloseOnSelect
      filterSelectedOptions
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionLabel}
      loading={loading}
      multiple
      onChange={handleChange}
      options={ids}
      renderInput={renderInput}
      renderOption={resolvedRenderOption}
      slotProps={{ chip: { size: 'small' } }}
      value={[...value]}
    />
  );
};
